import React, { useEffect, useState, Fragment } from "react";
import "../assets/styles/PoleInformation.scss";
import { toast } from "react-toastify";
import { updatePoleIssueStatusApi, undoRepairHistoryApi, saveCommentApi } from '../services/api';
import ConfirmPoleStatus from './ConfirmPoleStatus';
import { poleReplacementType } from "../types/customTypes";
import ConfirmPoleRepaireWithDate from "./ConfirmPoleRepaireWithDate";
import ConfirmMOdal from "./ConfirmModal";
import CategoryGroup from "./PoleInformation/CategoryGroup";
import CommentsSection from "./PoleInformation/CommentsSection";
import ReplacementHistory from "./PoleInformation/ReplacementHistory";
import GeneralInfo from "./PoleInformation/GeneralInfo";

type dataArray = {
	schoolName: string;
	pole_Number: string;
	type: string;
	material: string;
}

type recommendationArray = {
	high, medium, low: {
		repairId: number
		cat_Id: number;
		updatedOn: string; updatedBy: string;
		isResolved: boolean; isUpdateable: boolean;
		category: string, subCategory: string,
		remedy: string;
		repairTypes?: string[];
		selectedRepairType?: string;
		repairedOn?: string;
		description?: string;
	}[]
}

type commentArray = {
	comment: string;
	poleCommentId: string;
	isResolved: boolean;
	poleAction: boolean;
	createdBy: string;
	createdOn: string;
	updatedBy: string;
	updatedOn: string;
}

type saveCommentParams = {
	comment: string;
	poleAction: boolean;
	poleCommentId: string | null;
	isResolved: boolean;
}

type replacementHistoryArray = {
	categoryId: number;
	repairId: number;
	repairCategoryId: number
	updatedOn: string;
	updatedBy: string;
	isResolved: boolean;
	isUpdateable: boolean;
	category: string;
	subCategory: string;
	repairDescription: string;
};
export interface Props {
	poleid: unknown | string;
	data: dataArray[];
	recommendation: recommendationArray[];
	comments: commentArray[];
	replacementHistory: replacementHistoryArray[];
	refreshPoleData: () => void;
	refreshRepaireMatrix: () => void;
	isPoleReplacement: poleReplacementType;
	otherRepairId: number
}

const PoleInformation: React.FC<Props> = (props: Props) => {
	const [status, setStatus] = useState({ isResolved: false, repairId: null });
	const [modalShow, setModalShow] = useState(false);
	const [confirmModal, setConfirmModal] = useState(false);
	const [modalDateShow, setModalDateShow] = useState(false);
	const [confirmationParams, setConfirmationParams] = useState<saveCommentParams | null>(null);
	const [repairData, setRepairData] = useState<{ [key: number]: { repairType: string; repairDate: Date | null; description: string; repairId?: number } }>({});
	const [editingRepairId, setEditingRepairId] = useState<number | null>(null);

	// Clean up repairData for items that are now resolved
	useEffect(() => {
		if (props.recommendation && props.recommendation.length > 0) {
			const allRecommendations = [
				...props.recommendation[0]?.high || [],
				...props.recommendation[0]?.medium || [],
				...props.recommendation[0]?.low || []
			];
			
			setRepairData(prev => {
				const newData = { ...prev };
				allRecommendations.forEach(rec => {
					if (rec.isResolved && newData[rec.cat_Id]) {
						delete newData[rec.cat_Id];
					}
				});
				return newData;
			});
		}
	}, [props.recommendation]);


	const getFlooredFixed = (num: number | string | null | undefined) => {
		if (num === null || num === undefined || num === '') return '';
		const number = Number(num);
		if (isNaN(number)) return '';
		const factor = 10000;
		const floored = Math.floor(number * factor) / factor;
		return floored.toFixed(4).replace(/\.?0+$/, '');
	};


	const showPolePriority = (pole) => {
		if (pole.high > 0) return "High";
		if (pole.medium > 0) return "Medium";
		if (pole.low > 0) return "Low";
		else return "None"
	};

	const formateDate = (dateString) => { //format date and time in mm-dd-yyyy and HH:MM:SS respectively
		if (dateString) {
			const date = new Date(dateString);
			const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
			const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
			const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
			// const time = date.toLocaleTimeString('en-US', { hour12: false });
			return `${month}/${day}/${year} `;
		}
	}

	const formatDateAndTime = (inputDateString) => {
		const inputDate = new Date(inputDateString);
    
		const options = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hourCycle: "h12",
		} as Intl.DateTimeFormatOptions;

		const formattedDate = new Intl.DateTimeFormat('en-US', options).format(inputDate);
		return formattedDate;
	};

	const formatDateMMDDYY = (date: Date) => {
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const year = String(date.getFullYear()).slice(-2);
		const minutes = String(date.getMinutes()).padStart(2, '0');
		const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
		const hours12 = String(date.getHours() % 12 || 12).padStart(2, '0');
		return `${month}/${day}/${year} ${hours12}:${minutes} ${ampm}`;
	};

	const changePoleIssueStatus = (isResolved, cat_Id, repairId, repairTypes) => {
		// If repair data exists but not saved, cancel it
		if (repairData[cat_Id] && !isResolved) {
			setRepairData(prev => {
				const newData = { ...prev };
				delete newData[cat_Id];
				return newData;
			});
			return;
		}
		
		if (!isResolved) {
			// Check if repairTypes is an array and has items
			const availableRepairTypes = Array.isArray(repairTypes) ? repairTypes : [];
			setRepairData(prev => ({
				...prev,
				[cat_Id]: {
					repairType: availableRepairTypes[0] || "",
					repairDate: new Date(), // Default to current date
					description: "" // Initialize description as empty
				}
			}));
			// Don't call API yet - wait for user to confirm with repair type and date
		} else {
			// When toggling OFF an already resolved item, remove repair data and undo
			setRepairData(prev => {
				const newData = { ...prev };
				delete newData[cat_Id];
				return newData;
			});
			setStatus({ isResolved: isResolved, repairId: repairId });
			undoReplacePole();
		}
	}

	const handleRepairTypeChange = (cat_Id: number, repairType: string) => {
		setRepairData(prev => ({
			...prev,
			[cat_Id]: {
				...prev[cat_Id],
				repairType: repairType
			}
		}));
	}

	const handleRepairDateChange = (cat_Id: number, date: Date | null) => {
		setRepairData(prev => ({
			...prev,
			[cat_Id]: {
				...prev[cat_Id],
				repairDate: date
			}
		}));
	}

	const handleRepairDescriptionChange = (cat_Id: number, description: string) => {
		setRepairData(prev => ({
			...prev,
			[cat_Id]: {
				...prev[cat_Id],
				description: description
			}
		}));
	}

	const saveRepair = (cat_Id: number) => {
		const repairInfo = repairData[cat_Id];
		if (!repairInfo || !repairInfo.repairType || !repairInfo.repairDate) {
			toast.error("Please select repair type and date");
			return;
		}
		if (!repairInfo.repairType || repairInfo.repairType === "") {
			toast.error("Please select a valid repair type");
			return;
		}
		callUpdatePoleStatusWithRepair(cat_Id, repairInfo.repairType, repairInfo.repairDate, repairInfo.description, repairInfo.repairId);
		// Clear editing state
		setEditingRepairId(null);
	}

	const startEditRepair = (cat_Id: number, repairId: number, selectedRepairType: string, repairedOn: string, description: string, repairTypes: string[]) => {
		setEditingRepairId(repairId);
		setRepairData(prev => ({
			...prev,
			[cat_Id]: {
				repairType: selectedRepairType || (Array.isArray(repairTypes) && repairTypes.length > 0 ? repairTypes[0] : ""),
				repairDate: repairedOn ? new Date(repairedOn) : new Date(),
				description: description || "",
				repairId: repairId
			}
		}));
	}

	const cancelEditRepair = (cat_Id: number) => {
		setEditingRepairId(null);
		setRepairData(prev => {
			const newData = { ...prev };
			delete newData[cat_Id];
			return newData;
		});
	}

	const updateStatusOrReplacePoleApi = (params) => {
		updatePoleIssueStatusApi(params).then(response => {
			props.refreshPoleData();
			props.refreshRepaireMatrix();
			setModalShow(false);
			toast.success(response);
		}).catch(err => {
			toast.error(err);
		})
	};

	const callUpdatePoleStatusWithRepair = (cat_Id, repairType, repairDate, description, repairId?: number) => {
		const formattedDate = formatDateMMDDYY(repairDate);
		const params: any = {
			poleId: props.poleid,
			// repairCategoryId: props.otherRepairId,
			categoryId: cat_Id,
			repairType: repairType,
			repairedOn: formattedDate,
			description: description || null
		}
		// Include repairId if updating an existing repair
		if (repairId) {
			params.repairId = repairId;
		}
		updateStatusOrReplacePoleApi(params);
		// Clear repair data after successful save
		setRepairData(prev => {
			const newData = { ...prev };
			delete newData[cat_Id];
			return newData;
		});
	}

	const handleReplacePole = (datetime) => {
		const formattedDate = formatDateMMDDYY(datetime);
		const params = {
			poleId: props.poleid,
			// repairCategoryId: props.isPoleReplacement.poleReplacementRepairId,
			repairType: "Pole Replacement",
			categoryId: 0,
			submittedOn: formattedDate
		}
		updateStatusOrReplacePoleApi(params);
		setModalDateShow(false);
	};

	const undoReplacePole = () => {
		setModalShow(true);
	};

	const handleUndoRepairHistoryApi = (params) => {
		undoRepairHistoryApi(params).then(response => {
			props.refreshPoleData();
			props.refreshRepaireMatrix();
			setModalShow(false);
			toast.success(response);
			setStatus({ isResolved: false, repairId: null })
		}).catch(err => {
			toast.error(err);
		});
	};

	const confirmUndoReplacePole = () => {
		const repairIds = props.replacementHistory.map(data => data.repairId)
		const params = {
			poleId: props.poleid,
			repairId: repairIds
		}
		handleUndoRepairHistoryApi(params);
	};

	const UndoUpdatedPoleStatus = (repairId) => {
		const params = {
			poleId: props.poleid,
			repairId: [repairId]
		}
		handleUndoRepairHistoryApi(params);
	};

	// add comment, edit comment and change toggle status handler
	const onSubmitComment = (saveCommentParams: saveCommentParams) => {
		const params = {
			poleId: props.poleid,
			...saveCommentParams
		};
		saveCommentApi(params).then(() => {
			toast.success("Comment Updated Successfully!");
			props.refreshPoleData();
		}).catch(err => {
			console.error(err);
			toast.error(err);
		});
	};

	const onCommentClose = (saveCommentParams: saveCommentParams) => {
		// Set saveCommentParams to a state variable
		setConfirmationParams(saveCommentParams);
		setConfirmModal(true);
	};

	const saveCommentApiFunc = (confirmationParams) => {
		const params = {
			poleId: props.poleid,
			...confirmationParams
		};
		saveCommentApi(params).then(() => {
			toast.success("Comment Updated Successfully!");
			props.refreshPoleData();
			setConfirmationParams(null);
			setConfirmModal(false);
		}).catch(err => {
			console.error(err);
			toast.error(err);
		});
	};


	return (
		<div className="pole-info" id="poleinfo">
			{props.poleid
				? (
					<>
						<Fragment>
							<div className="poleinfo mb-2" data-testid="poleinfo">
								{props.data.map((data, index) => (
									<GeneralInfo
										key={index}
										data={data}
										getFlooredFixed={getFlooredFixed}
										showPolePriority={showPolePriority}
									/>
								))}
								{props.recommendation.map((data, index) => (
									<CategoryGroup
										key={index}
										data={data}
										repairData={repairData}
										editingRepairId={editingRepairId}
										onStatusChange={changePoleIssueStatus}
										onRepairTypeChange={handleRepairTypeChange}
										onRepairDateChange={handleRepairDateChange}
										onRepairDescriptionChange={handleRepairDescriptionChange}
										onSaveRepair={saveRepair}
										onCancelEdit={cancelEditRepair}
										onStartEdit={startEditRepair}
										formatDate={formateDate}
									/>
								))}

								{props.comments && (
									<CommentsSection
										comments={props.comments}
										poleId={props.poleid}
										onCommentSubmit={onSubmitComment}
										onCommentDelete={onCommentClose}
									/>
								)}

								{props.data.length > 0 && (
									<ReplacementHistory
										replacementHistory={props.replacementHistory}
										isPoleReplacement={props.isPoleReplacement}
										onUndoReplace={undoReplacePole}
										onReplacePole={() => setModalDateShow(true)}
										formatDateAndTime={formatDateAndTime}
									/>
								)}
							</div>
						</Fragment>
					</>
				)
				: (
					<div data-testid="alert" className="alert alert-warning alert-dismissible fade show mt-2" role="alert">
						Please select pole marker to view pole information.
					</div>
			)}
			<ConfirmMOdal
				id="deletePoleCommentConfirmModal"
				data-testid="deletePoleCommentConfirmModal"
				message="Are you sure you want to delete this pole comment?"
				confirm={() => {
					if (confirmationParams) {
						saveCommentApiFunc(confirmationParams);
					}
				}}
				show={confirmModal}
				onHide={() => setConfirmModal(false)}
			/>
			
			<ConfirmPoleStatus
				status={status}
				show={modalShow}
				onHide={() => setModalShow(false)}
				UndoUpdatedPoleStatus={UndoUpdatedPoleStatus}
				confirmUndoReplacePole={confirmUndoReplacePole}
			/>
			<ConfirmPoleRepaireWithDate
				show={modalDateShow}
				onHide={() => setModalDateShow(false)}
				handleReplacePole={handleReplacePole}
			/>
		</div >
	);
}

export default PoleInformation;
